import { Box, Button, CircularProgress, Paper } from "@material-ui/core";
import { ImportDialogButton } from "components/dialogs/ImportDialog/ImportDialogButton";
import { DateFormatField } from "components/fields/DateFormatField";
import { FakeReferenceTextField } from "components/fields/FakeReferenceTextField";
import { ProductArticleReferenceField } from "components/fields/ProductArticleReferenceField";
import { SelectEnumField } from "components/fields/SelectEnumField";
import { YesNoField } from "components/fields/YesNoField";
import { ProgressIcon } from "components/ProgressIcon";
import { ScrollingWrapper } from "components/ScrollingWrapper";
import { DeleteAllButton } from "components/toolbars/DeleteAllButton";
import { DeleteButton } from "components/toolbars/DeleteButton";
import { PalletType } from "entities/PalletType";
import React, { useState } from "react";
import {
	Datagrid,
	FunctionField,
	NumberField,
	TextField,
	useListContext,
	usePermissions,
	useTranslate,
} from "react-admin";
import { Link } from "react-router-dom";
import { getOrderPalletsPath } from "utils/navigation";

import { UserRole } from "../../../entities";
import {
	OrderStatusAddDeleteOrderProductArticle,
	OrderStatusEditOnlyDateOrderProductArticle,
	OrderStatusEditOrderProductArticle,
	OrderStatusIncomingPalletGenerationAllowed,
} from "../../../entities/OrderStatus";
import { AddOrderProductArticle } from "./AddOrderProductArticle";
import { GenerateAllPalletsButton } from "./GenerateAllPalletsButton";
import { GeneratePalletsButton } from "./GeneratePalletsButton";
import { UpdateOrderProductArticle } from "./UpdateOrderProductArticle";

export const OrderProductArticlesList = ({
	orderId,
	companyId,
	pallets,
	status,
	totalBoxes,
	...props
}) => {
	const [productArticleReloadKey, setProductArticleReloadKey] = useState(0);
	const disabledGeneratePallets = !OrderStatusIncomingPalletGenerationAllowed.includes(status);
	const disableAddDelete = !OrderStatusAddDeleteOrderProductArticle.includes(status);
	const disableEdit = !OrderStatusEditOrderProductArticle.includes(status);
	const allowEditOnlyDate = OrderStatusEditOnlyDateOrderProductArticle.includes(status);
	const translate = useTranslate();
	const { refetch } = useListContext();
	const { permissions } = usePermissions();

	if (props.loading) {
		return (
			<Paper style={{ width: "100%", padding: "2rem", display: "flex", justifyContent: "center" }}>
				<CircularProgress />
			</Paper>
		);
	}

	return (
		<Paper style={{ width: "100%" }}>
			<Box
				display="flex"
				justifyContent="flex-end"
				alignItems="center"
				p={1}
				style={{ gap: "1em" }}
			>
				{/* TODO - temporary button */}
				{permissions.role === UserRole.admin && (
					<ImportDialogButton
						label="osfc.buttons.order-product-article-custom-export-products"
						dialogTitle="osfc.dialogs.order-product-articles-custom-export-products.title"
						uploadEndpoint="storage-data-upload-export-product"
						successMessage="osfc.dialogs.order-product-articles-custom-export-products.success"
						initialValues={{ order_id: orderId }}
						acceptTypes=".xlsx"
						exportMode={true}
						exportButtonLabel="osfc.dialogs.order-product-articles-custom-export-products.button.export"
						exportFileName={`product_export_order_${orderId}.xlsx`}
					/>
				)}
				{permissions.role === UserRole.admin && (
					<ImportDialogButton
						label="osfc.buttons.order-product-article-custom-import"
						dialogTitle="osfc.dialogs.order-product-articles-custom-import-upload.title"
						uploadEndpoint="storage-data-upload"
						successMessage="osfc.dialogs.order-product-articles-import-upload.success"
						onSuccess={refetch}
						initialValues={{ order_id: orderId, is_custom_import: true }}
						acceptTypes=".xlsx"
					/>
				)}
				{permissions.role === UserRole.admin && (
					<ImportDialogButton
						label="osfc.buttons.import"
						dialogTitle="osfc.dialogs.order-product-articles-import-upload.title"
						uploadEndpoint="storage-data-upload"
						successMessage="osfc.dialogs.order-product-articles-import-upload.success"
						onSuccess={refetch}
						initialValues={{ order_id: orderId }}
						acceptTypes=".xlsx"
					/>
				)}
				{permissions.role === UserRole.admin && (
					<DeleteAllButton
						resource="order-product-articles-bulk-delete"
						data={{ order_id: orderId }}
						label="osfc.buttons.delete-all"
						title="osfc.dialogs.confirm_delete_all_order_product_articles.confirm.title"
						content="osfc.dialogs.confirm_delete_all_order_product_articles.confirm.content"
						successMessage="osfc.dialogs.confirm_delete_all_order_product_articles.success"
						onSuccess={refetch}
						disabled={disableAddDelete}
					/>
				)}
				<GenerateAllPalletsButton
					orderId={orderId}
					disabledGenerateAllPallets={disabledGeneratePallets}
					orderProductArticles={props.data || {}}
				/>
				<Button
					component={Link}
					to={getOrderPalletsPath(orderId)}
					color="secondary"
					variant="contained"
					size="small"
				>
					{translate("resources.order-product-articles.fields.go-to-pallets-button")}
				</Button>
				<AddOrderProductArticle
					orderId={orderId}
					companyId={companyId}
					productArticleReloadKey={productArticleReloadKey}
					setProductArticleReloadKey={setProductArticleReloadKey}
					totalBoxes={totalBoxes}
					allOrderProductArticles={props.data}
					disableAdd={disableAddDelete}
				/>
			</Box>
			<ScrollingWrapper>
				<Datagrid>
					<ProductArticleReferenceField
						source="product_number"
						companyId={companyId}
						sortable={false}
						label="resources.product-articles.fields.product_number"
					/>
					<ProductArticleReferenceField
						source="product_name"
						companyId={companyId}
						sortable={false}
						label="resources.product-articles.fields.product_name"
					/>
					<FakeReferenceTextField
						source="commodity_id"
						replaceSource="commodity_name"
						sortable={false}
						label="resources.product-articles.fields.commodity_id"
					/>
					<TextField
						source="batch_number"
						label="resources.order-product-articles.fields.batch_number"
					/>
					<TextField source="brand" label="resources.order-product-articles.fields.brand" />
					<NumberField
						source="box_weight"
						options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
						label="resources.order-product-articles.fields.box_weight"
					/>
					<NumberField source="quantity" label="resources.order-product-articles.fields.quantity" />
					<DateFormatField
						source="production_date"
						label="resources.order-product-articles.fields.production_date"
					/>
					<DateFormatField
						source="expiration_date"
						label="resources.order-product-articles.fields.expiration_date"
					/>
					<NumberField
						source="unit_per_pallet"
						label="resources.order-product-articles.fields.unit_per_pallet"
					/>
					<NumberField
						source="weight_per_pallet"
						options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
						label="resources.order-product-articles.fields.weight_per_pallet"
					/>
					<NumberField
						source="total_weight"
						options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
						label="resources.order-product-articles.fields.total_weight"
					/>
					<YesNoField
						source="is_need_place_pallets_nearby"
						label="resources.order-product-articles.fields.is_need_place_pallets_nearby"
						sortable={false}
					/>
					<YesNoField
						source="is_t1"
						label="resources.order-product-articles.fields.is_t1"
						sortable={false}
					/>
					<SelectEnumField
						source="pallet_type"
						enumName="pallet_type"
						enumObj={PalletType}
						label="resources.order-product-articles.fields.pallet_type"
					/>
					<TextField
						source="ceel_number"
						label="resources.order-product-articles.fields.ceel_number"
					/>
					<FunctionField
						render={(record) => (
							<UpdateOrderProductArticle
								orderProductArticle={record}
								companyId={companyId}
								productArticleReloadKey={productArticleReloadKey}
								setProductArticleReloadKey={setProductArticleReloadKey}
								totalBoxes={totalBoxes}
								allOrderProductArticles={props.data}
								disableEdit={disableEdit}
								allowEditOnlyDate={allowEditOnlyDate}
							/>
						)}
					/>
					<FunctionField
						render={(record) => {
							return (
								<GeneratePalletsButton
									orderProductArticle={record}
									disabledGeneratePallets={disabledGeneratePallets}
								/>
							);
						}}
					/>
					<FunctionField
						render={(record) => (
							<DeleteButton
								resource={props.resource}
								notifyMessage="osfc.dialogs.confirm_delete_order_product_articles.success"
								title="osfc.dialogs.confirm_delete_order_product_articles.confirm.title"
								content="osfc.dialogs.confirm_delete_order_product_articles.confirm.content"
								disableDelete={disableAddDelete}
							/>
						)}
					/>
				</Datagrid>
			</ScrollingWrapper>
		</Paper>
	);
};
